import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import bannerDataMapper from "@/mappers/bannerDataMapper";
import {
    BannerData as BannerDataSDK,
    useSdk,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useGetEventBus from "./useGetEventBus";

const useGetBanner = () => {
    const queryClient = useQueryClient();
    const { bannerManager } = useSdk();

    const fetchBanner = async () => {
        const banner = bannerManager.getCurrentBanner();

        return banner ? bannerDataMapper(banner) : null;
    };

    const getBanner = useQuery({
        queryKey: queryKeys.bannerData(),
        queryFn: fetchBanner,
    });

    // La queryFn lit l'état mémoire du SDK : un refetch ne revérifierait rien.
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible") {
                return;
            }

            bannerManager.refreshBanner();
        };

        // `visibilitychange` ne couvre que l'onglet caché, pas le retour sur la fenêtre.
        const handleWindowFocus = () => bannerManager.refreshBanner();

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleWindowFocus);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange,
            );
            window.removeEventListener("focus", handleWindowFocus);
        };
    }, [bannerManager]);

    // `null` = plus de bannière à afficher.
    const handleBannerUpdated = (newBanner: BannerDataSDK | null) => {
        queryClient.setQueryData(
            queryKeys.bannerData(),
            newBanner ? bannerDataMapper(newBanner) : null,
        );
    };

    const { off: offBannerUpdated } = useGetEventBus<BannerDataSDK | null>(
        eventKeys.banner.updated,
        handleBannerUpdated,
    );

    useEffect(
        () => () => {
            offBannerUpdated(eventKeys.banner.updated, handleBannerUpdated);
        },
        [],
    );

    return getBanner;
};

export default useGetBanner;
